import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  ConversationListCursor,
  ConversationListItemResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type {
  MessageResponse,
  MessageWithConversationId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { eq } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { useCallback, useMemo } from "react";
import { fetchConversationsPage } from "../db/collections";
import { useGlobalConversationCollections } from "../providers/support-providers";
import { DEFAULT_PAGE_SIZE } from "../table/types";
import { useLiveInfiniteCursorQuery } from "./use-live-infinite-cursor-query";

export interface UseConversationListResult {
  conversations: ConversationListItemResponse[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

export function useConversations(): {
  data: ConversationListItemResponse[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
} {
  const collections = useGlobalConversationCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const fetchPage = useCallback(
    async (cursor: ConversationListCursor | null) => {
      const result = await fetchConversationsPage(effectApi, {
        limit: DEFAULT_PAGE_SIZE,
        cursor: cursor ?? undefined,
      });

      for (const item of result.items) {
        collections.conversationsList.utils.writeInsert(item);
      }

      return { items: result.items, nextCursor: result.nextCursor };
    },
    [effectApi, collections.conversationsList.utils],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useLiveInfiniteCursorQuery(
      (q) =>
        q
          .from({ conversation: collections.conversationsList })
          .orderBy(
            ({ conversation }) => conversation.lastCustomerMessageAt,
            "desc",
          ),
      {
        pageSize: DEFAULT_PAGE_SIZE,
        getCursor: (lastItem) => {
          if (!lastItem) {
            return null;
          }
          const conversation = lastItem;
          if (!conversation.id) {
            return null;
          }
          return {
            lastCustomerMessageAt: conversation.lastCustomerMessageAt,
            id: conversation.id,
          };
        },
        fetchPage,
      },
      [],
    );

  const conversations = useMemo(() => {
    return data.filter(
      (row): row is ConversationListItemResponse => row != null && "id" in row,
    );
  }, [data]);

  return {
    data: conversations,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}

export function useConversation(conversationId: ConversationId): {
  data: ConversationListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const result = useLiveQuery((q) =>
    q
      .from({ conversation: collections.conversationsList })
      .where(({ conversation }) => eq(conversation.id, conversationId))
      .findOne(),
  );

  //todo: improve isLoading state handling
  return { data: result.data, isLoading: false };
}

export function useConversationThreads(conversationId: ConversationId): {
  data: ConversationThreadResponse[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.conversationId, conversationId)),
    [conversationId],
  );

  return { data: result.data, isLoading: result.isLoading };
}

export function useConversationMessages(conversationId: ConversationId): {
  data: MessageWithConversationId[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId)),
    [conversationId],
  );

  const data = (result.data ?? []).filter(
    (message): message is MessageWithConversationId => message !== undefined,
  );
  return { data, isLoading: result.isLoading };
}

export function useThreadMessages(threadId: string): {
  data: MessageResponse[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationThreadId, threadId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [threadId],
  );

  return { data: result.data, isLoading: result.isLoading };
}

export interface ConversationDataRow {
  conversation: ConversationListItemResponse;
  thread: ConversationThreadResponse | undefined;
  message: MessageResponse | undefined;
}

export function useSingleConversationData(conversationId: ConversationId): {
  conversation: ConversationListItemResponse | undefined;
  threads: ConversationThreadResponse[];
  messages: MessageResponse[];
  isLoading: boolean;
} {
  const conversationResult = useConversation(conversationId);
  const threadsResult = useConversationThreads(conversationId);
  const messagesResult = useConversationMessages(conversationId);

  //todo:  there seems to be behavior where if the collections are cached, then is loading is infinitely set to true. handle that
  return {
    conversation: conversationResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    isLoading: false, //threadsResult.isLoading || messagesResult.isLoading ,
  };
}
